import React, { useContext } from 'react';

import { AppContext } from 'src/components/app-context';
import { useProductContext } from '../../../product-context';

import { CarouselProducts } from '../../../carousel-products';
import { CarouselTypeEnum } from '../../../carousel-products/types';
import { OtherAlsoVisitedCarousel } from '../../../carousel-products/OtherAlsoVisitedCarousel';
import { PageSection } from 'src/components/page';
import { ScrollElement } from 'src/components/scroll';
import { UxLiteQuestions } from 'src/components/ux-feedback';
import { PriceList } from '../../../price-list';
import { ReadableProductInformation } from '../../../readable-product-information';

import { ONE_DAY, useShowUXFeedback } from 'src/hooks/use-show-ux-feedback';
import { useLocalStorage } from 'src/hooks/use-localstorage';

import type { CarouselProduct } from 'src/types';
import type { CurrentCampaign } from 'src/areas/product/types';
import type { IProductTabMainProps } from 'src/areas/product/components/product-tabs/types';

import { StyledPageSection } from './styles';

export const ProductTabMain: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IProductTabMainProps>>
> = props => {
  const { recommendations, currentCampaign } = props;
  const { product } = useProductContext();
  const priceList = product.prices;
  const [productUXFeedbackTime] = useLocalStorage(
    'pj:productUXFeedbackTime',
    0
  );
  const showUXFeedback = useShowUXFeedback(
    ONE_DAY * 7,
    productUXFeedbackTime,
    0.5
  );

  const { t } = useContext(AppContext);

  const displayTrendingProducts = (product.trendingProducts || []).length > 0;
  const displayOtherVisited = (recommendations || []).length > 0;

  const displayPopularProducts =
    !displayTrendingProducts &&
    !displayOtherVisited &&
    (product.popularProducts || []).length > 0;

  return (
    <>
      <StyledPageSection noPad="top" recessed>
        {priceList && (
          <ScrollElement name="Pricelist" data-test="MainTabContent">
            <PriceList
              priceList={priceList}
              campaign={currentCampaign as CurrentCampaign}
            />
          </ScrollElement>
        )}

        {displayOtherVisited && (
          <OtherAlsoVisitedCarousel recommendations={recommendations} />
        )}

        {displayTrendingProducts && (
          <CarouselProducts
            title={t('landing', 'trending-products.title')}
            products={product.trendingProducts as CarouselProduct[]}
            carouselType={CarouselTypeEnum.TRENDING_PRODUCTS}
          />
        )}

        {displayPopularProducts && (
          <CarouselProducts
            title={t('product', 'popular-products.title')}
            products={product.popularProducts as CarouselProduct[]}
            carouselType={CarouselTypeEnum.OTHER_POPULAR_PRODUCTS}
          />
        )}

        {showUXFeedback && <UxLiteQuestions page="product" />}
      </StyledPageSection>
      <PageSection noBorder>
        <ReadableProductInformation />
      </PageSection>
    </>
  );
};
